import { useState } from "react";
import { genElDate } from "../../../../components/utils/EditorDialogElementGenerator";
import { endOfMonth, format, startOfMonth } from "date-fns";
import useIsMobile from "../../../../hooks/useIsMobile";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import Tooltip from "../../../../utils/element-generator/Tooltip";
import { Skeleton } from "primereact/skeleton";
import { AnimatePresence, motion } from "framer-motion";

const CustomerSalesHistory = (props) => {
  const { data, loading, error, filterValue, setFilterValue } = props;

  const isMobile = useIsMobile(750);

  const [date, setDate] = useState({
    dari_tanggal: format(startOfMonth(new Date()), "yyyy-MM-dd HH:mm:ss"),
    sampai_tanggal: format(endOfMonth(new Date()), "yyyy-MM-dd HH:mm:ss"),
  });

  const elStartDate = genElDate({
    name: "Start Date",
    field: "dari_tanggal",
    max: new Date(),
    data: date,
    setData: setDate,
    error,
    disabled: loading,
    disableKeyboard: true,
    showIcon: false,
    customLabelCSS: "text-sm text-black font-medium",
  });

  const elEndDate = genElDate({
    name: "End Date",
    field: "sampai_tanggal",
    data: date,
    setData: setDate,
    error,
    disabled: loading,
    disableKeyboard: true,
    showIcon: false,
    customLabelCSS: "text-sm text-black font-medium",
  });

  const TableSkeleton = ({ rows = 10 }) => {
    const skeletonRows = Array.from({ length: rows }, (_, i) => ({ id: i }));

    // Mobile card-style skeleton
    const mobileCardBody = () => (
      <div className="flex flex-col gap-2 w-full">
        <div className="flex flex-row items-center gap-2 w-full">
          <Skeleton shape="square" size="1.5rem" className="flex-shrink-0" />
          <Skeleton height="1rem" width="100%" className="flex-1" />
        </div>
        <Skeleton width="6rem" height="1rem" />
        <Skeleton width="8rem" height="1rem" />
      </div>
    );

    // Table view skeletons
    const nameSkeleton = () => (
      <div className="flex items-center gap-2 w-full">
        {/* Skeleton for the number (like 1., 2., etc.) */}
        <Skeleton
          width="2rem"
          height="2rem"
          className="rounded-md flex-shrink-0"
        />

        {/* Skeleton for the product name */}
        <Skeleton height="1rem" width="100%" />
      </div>
    );

    const quantitySkeleton = () => (
      <div className="flex justify-start">
        <Skeleton width="5rem" height="1rem" />
      </div>
    );

    const totalSkeleton = () => (
      <div className="flex justify-start">
        <Skeleton width="6rem" height="1rem" />
      </div>
    );

    return (
      <div className="w-full rounded-xl overflow-hidden">
        {isMobile ? (
          <DataTable value={skeletonRows} className="w-full" stripedRows>
            <Column
              header="Name"
              body={mobileCardBody}
              headerStyle={{ display: "none" }}
            />
          </DataTable>
        ) : (
          <DataTable value={skeletonRows} className="w-full" stripedRows>
            <Column
              header="Name"
              body={nameSkeleton}
              style={{ width: "60%" }} // Make Name column take more space
            />
            <Column
              header="Quantity"
              body={quantitySkeleton}
              style={{ width: "20%" }} // Adjust as needed
            />
            <Column
              header="Total"
              body={totalSkeleton}
              style={{ width: "20%" }} // Adjust as needed
            />
          </DataTable>
        )}
      </div>
    );
  };

  const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatPax = (value) => `${value} pax`;

  const mobileBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="col-center-start gap-2 w-full">
        {/* Row number */}
        <div className="row-start-start gap-2 w-full">
          <span
            className={`flex items-center justify-center text-sm font-bold text-purple-500`}
          >
            {no}.
          </span>

          {/* Product Name */}

          <span
            className={`w-full font-semibold text-black text-sm`} // Truncate product name
          >
            {rowData.product_name}
          </span>
        </div>

        {/* Quantity */}
        <span className="text-sm text-gray-700 font-semibold ">
          Quantity: {formatPax(rowData.total_quantity)}
        </span>

        {/* Total Price */}
        <span className="text-sm text-gray-700 font-semibold">
          Total:{" "}
          {rowData.grand_total === 0
            ? "Free Product"
            : formatCurrency(rowData.grand_total)}
        </span>
      </div>
    );
  };

  const nameBodyTemplate = (rowData, event) => {
    const { rowIndex } = event;
    const no = rowIndex + 1;

    return (
      <div className="row-start-center gap-2">
        <span
          className={`min-w-[3rem] min-h-[3rem] w-[3rem] h-[3rem] flex items-center justify-center text-md font-bold text-purple-500`}
        >
          {no}.
        </span>
        <span className="w-full font-semibold text-sm">
          {rowData.product_name}
        </span>
      </div>
    );
  };

  const grandTotalBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full text-sm">
        {rowData?.grand_total === 0
          ? "Free Product"
          : formatCurrency(rowData.grand_total)}
      </div>
    );
  };

  const quantityBodyTemplate = (rowData) => {
    return (
      <div className="font-semibold w-full text-sm">
        {formatPax(rowData.total_quantity)}
      </div>
    );
  };

  return (
    <div className="col-center-start w-full">
      <div className="col-start-center w-full">
        <div className="flex flex-col md:flex-row justify-center items-end gap-2 w-full font-medium">
          {elStartDate}
          {elEndDate}
          <div className="w-full row-end-center gap-2 mt-2">
            <Button
              icon="pi pi-refresh"
              label="Load Report"
              loading={loading}
              className="w-full md:w-auto sm:max-h-[3rem] rounded-md bg-gradient-to-br from-green-400 to-emerald-500 text-white font-semibold shadow-md hover:shadow-lg border-none"
              onClick={() => {
                setFilterValue({
                  ...filterValue,
                  dari_tanggal: date.dari_tanggal,
                  sampai_tanggal: date.sampai_tanggal,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="col-center-start gap-2 p-2 mt-4">
        <div className="w-full row-start-center gap-4">
          <i className="pi pi-wallet text-lg sm:text-2xl text-green-500"></i>

          <AnimatePresence mode="wait">
            <span className="flex flex-row justify-start items-center gap-2 text-md sm:text-xl font-semibold text-green-500 overflow-wrap">
              {loading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Skeleton width="10rem" height="1.75rem" />
                </motion.div>
              ) : (
                <motion.div
                  key="data"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  Customer Total Spend :{" "}
                  <Tooltip text="TOTAL SEBELUM DISKON BON">
                    {formatCurrency(data?.grand_total_purchase) || "IDR 0"}
                  </Tooltip>
                </motion.div>
              )}
            </span>
          </AnimatePresence>
        </div>
      </div>

      <div className="w-full border border-gray-300 rounded-xl overflow-hidden mt-4">
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="skeleton"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <TableSkeleton rows={10} />
            </motion.div>
          ) : isMobile ? (
            <motion.div
              key="mobile-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <DataTable
                value={data?.items}
                loading={loading}
                className="w-full"
                stripedRows
                selectionMode="single"
                rowClassName={() =>
                  "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md w-full"
                }
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
                paginatorTemplate={{
                  layout: isMobile
                    ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                    : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
                }}
              >
                <Column
                  header="Name"
                  body={mobileBodyTemplate}
                  headerStyle={{ display: "none" }}
                />
              </DataTable>
            </motion.div>
          ) : (
            <motion.div
              key="desktop-table"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <DataTable
                value={data?.items}
                loading={loading}
                className="w-full"
                stripedRows
                selectionMode="single"
                rowClassName={() =>
                  "transition-transform duration-300 hover:-translate-y-1 rounded-md hover:shadow-md"
                }
                removableSort
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 30, 50, 100, 500]}
                paginatorTemplate={{
                  layout: isMobile
                    ? "PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
                    : "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown",
                }}
              >
                <Column
                  field="product_name"
                  header="Name"
                  body={nameBodyTemplate}
                  style={{ width: "60%" }}
                />
                <Column
                  sortable
                  field="total_quantity"
                  header="Quantity"
                  body={quantityBodyTemplate}
                />
                <Column
                  sortable
                  field="grand_total"
                  header="Total"
                  body={grandTotalBodyTemplate}
                />
              </DataTable>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CustomerSalesHistory;
